import React, { useState, useEffect } from 'react';
import { Search, Filter, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import logo from '../assets/logo.png';

import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";



const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const budgetProducts = products.filter(p => p.price < 999);
  const getDailySeed = () => {
    const today = new Date().toDateString();
    let hash = 0;
    for (let i = 0; i < today.length; i++) {
      hash = today.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
  };
  const seededShuffle = (array) => {
    let seed = getDailySeed();
    return [...array].sort(() => {
      const x = Math.sin(seed++) * 10000;
      return x - Math.floor(x);
    });
  };
  const categories = ['All', 'Study', 'Decor', 'Kitchen', 'Fashion', 'Plants'];

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "products"), (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setProducts(data);
    });

    return () => unsubscribe(); // cleanup
  }, []);

  const trendingProducts = seededShuffle(
    products.filter(p => p.trending)
  ).slice(0, 6);

  const featuredProducts = products.filter(
    p => p.featured && p.price < 999
  );

  const filteredProducts = products
    .filter(p =>
      selectedCategory === "All" || p.category === selectedCategory
    )
    .filter(p =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === "price_low") return a.price - b.price;
      if (sortBy === "price_high") return b.price - a.price;
      return 0;
    });

  const ProductCard = ({ product }) => (
    <div
      id={`product-${product.id}`}
      className="bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
    >
      <div className="bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1" style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.04)' }} data-testid={`product-card-${product.id}`}>
        <div className="h-36 sm:h-44 lg:h-56 w-full overflow-hidden rounded-t-2xl bg-white">
          <img
            src={product.image}
            alt={product.name}
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/300x300?text=No+Image";
            }}
            className="h-full w-full object-contain transition-transform duration-300 hover:scale-105"
          />
        </div>
        <div className="p-3 sm:p-4 lg:p-5">
          <h3 className="text-sm sm:text-base lg:text-lg font-medium text-[#2C302E] mb-1 sm:mb-2 line-clamp-2 min-h-[40px] sm:min-h-[48px]" data-testid="product-name">{product.name}</h3>
          <p className="text-base sm:text-lg lg:text-xl font-semibold text-[#8CA692] mb-2 sm:mb-4" data-testid="product-price">₹{product.price}</p>
          <a
            href={product.amazon_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-on-amazon-btn"
          >
            <Button
              className="w-full rounded-lg py-2 sm:py-3 text-xs sm:text-sm lg:text-base font-medium"
              style={{
                backgroundColor: '#8CA692',
                color: 'white',
                transition: 'all 200ms'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#768F7B'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#8CA692'}
            >
              View on Amazon
            </Button>
          </a>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const productId = params.get("product");

    if (productId) {
      setTimeout(() => {
        const el = document.getElementById(`product-${productId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 1000); // wait for products to load
    }
  }, [products]);


  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F6F0' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl border-b"
        style={{ backgroundColor: 'rgba(249, 246, 240, 0.8)', borderColor: 'rgba(44, 48, 46, 0.05)' }}
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">

            <div className="flex items-center h-full">
              <img
                src={logo}
                alt="CartWhisper"
                className="h-10 md:h-12 w-auto object-contain cursor-pointer"
                onClick={() => {
                  setSelectedCategory("All");
                  document.getElementById("home").scrollIntoView({ behavior: "smooth" });
                }}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-6">

              <button onClick={() => {
                setSelectedCategory("All");
                document.getElementById("home").scrollIntoView({ behavior: "smooth" });
              }}>
                Home
              </button>
              <button onClick={() => {
                setSelectedCategory("All");
                document.getElementById("trending").scrollIntoView({ behavior: "smooth" });
              }}>
                Trending
              </button>

              <button onClick={() => {
                setSelectedCategory("Study");
                document.getElementById("products").scrollIntoView({ behavior: "smooth" });
              }}>
                Study
              </button>

              <button onClick={() => {
                setSelectedCategory("Decor");
                document.getElementById("products").scrollIntoView({ behavior: "smooth" });
              }}>
                Decor
              </button>
              <button onClick={() => {
                setSelectedCategory("Kitchen");
                document.getElementById("products").scrollIntoView({ behavior: "smooth" });
              }}>
                Kitchen
              </button>

              <button onClick={() => {
                setSelectedCategory("Fashion");
                document.getElementById("products").scrollIntoView({ behavior: "smooth" });
              }}>
                Fashion
              </button>

              <button onClick={() => {
                setSelectedCategory("Plants");
                document.getElementById("products").scrollIntoView({ behavior: "smooth" });
              }}>
                Plants & Gardening
              </button>

            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t" style={{ borderColor: 'rgba(44, 48, 46, 0.05)', backgroundColor: '#F9F6F0' }} data-testid="mobile-nav">
            <div className="px-4 py-4 flex flex-col space-y-3">
              <button onClick={() => {
                setSelectedCategory("All");
                document.getElementById("home").scrollIntoView({ behavior: "smooth" });
              }}>Home</button>
              <button onClick={() => {
                setSelectedCategory("All");
                document.getElementById("trending").scrollIntoView({ behavior: "smooth" });
              }}>
                Trending
              </button>
              <button onClick={() => {
                setSelectedCategory("Study");
                document.getElementById("trending").scrollIntoView({ behavior: "smooth" });
              }}>Study</button>
              <button onClick={() => {
                setSelectedCategory("Decor");
                document.getElementById("products").scrollIntoView({ behavior: "smooth" });
              }}>Decor</button>
              <button onClick={() => {
                setSelectedCategory("Kitchen");
                document.getElementById("products").scrollIntoView({ behavior: "smooth" });
              }}>Kitchen</button>
              <button onClick={() => {
                setSelectedCategory("Fashion");
                document.getElementById("products").scrollIntoView({ behavior: "smooth" });
              }}>Fashion</button>
              <button onClick={() => {
                setSelectedCategory("Plants");
                document.getElementById("products").scrollIntoView({ behavior: "smooth" });
              }}>Plants & Gardening</button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 scroll-mt-24" id="home" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold mb-6"
                style={{ color: '#2C302E' }}
                data-testid="hero-title"
              >
                Smart Finds for Everyday Living
              </h1>
              <p className="text-base leading-relaxed mb-8" style={{ color: '#5B635E' }} data-testid="hero-subtitle">
                Discover curated products that make your daily life better. From study essentials to home decor, we handpick the best deals on Amazon just for you.
              </p>
              <Button
                className="rounded-xl py-6 px-8 text-base font-medium tracking-wide"
                style={{
                  backgroundColor: '#8CA692',
                  color: 'white',
                  transition: 'all 200ms'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#768F7B'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#8CA692'}
                onClick={() => document.getElementById('trending').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-btn"
              >
                Explore Now
              </Button>
            </div>
            <div className="relative" data-testid="hero-image">
              <div className="relative rounded-3xl overflow-hidden" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/87cb6116-e2eb-4a7d-ac2e-808277879dd1/images/f525a1a3644d15fc171b8ccfb3cd54d55ef622e79bfb54a5f3ea2755a0541ff6.png"
                  alt="Hero"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.1), rgba(255,255,255,0.05))' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Finds Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 scroll-mt-20" id="trending" style={{ backgroundColor: '#FAF8F5' }} data-testid="trending-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-12" style={{ color: '#2C302E' }} data-testid="trending-title">
            Trending Finds
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {trendingProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Search, Filter & Sort Section */}
      <section id="products" className="py-16 px-4 sm:px-6 lg:px-8 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 space-y-4">
            {/* Search Bar */}
            <div className="relative" data-testid="search-bar">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2" style={{ color: '#8A948E' }} size={20} />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 rounded-xl border"
                style={{ borderColor: 'rgba(44, 48, 46, 0.15)', backgroundColor: 'white' }}
                data-testid="search-input"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-3" data-testid="category-filter">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className="px-6 py-3 rounded-xl text-base font-medium"
                  style={{
                    backgroundColor: selectedCategory === category ? '#8CA692' : 'white',
                    color: selectedCategory === category ? 'white' : '#2C302E',
                    border: `1px solid ${selectedCategory === category ? '#8CA692' : 'rgba(44, 48, 46, 0.1)'}`,
                    transition: 'all 200ms'
                  }}
                  data-testid={`category-btn-${category.toLowerCase()}`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-3" data-testid="sort-section">
              <Filter size={20} style={{ color: '#8A948E' }} />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48 rounded-xl" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest" data-testid="sort-newest">Newest First</SelectItem>
                  <SelectItem value="price_low" data-testid="sort-price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high" data-testid="sort-price-high">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* All Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">            {filteredProducts.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
          </div>
        </div>
      </section>

      {/* Featured Budget Finds */}
      {featuredProducts.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#FAF8F5' }} data-testid="featured-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-12" style={{ color: '#2C302E' }} data-testid="featured-title">
              Budget Finds Under ₹999
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">              {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t" style={{ borderColor: 'rgba(44, 48, 46, 0.08)' }} data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-medium mb-4" style={{ color: '#2C302E' }}>About CartWhisper</h3>
              <p className="text-sm" style={{ color: '#8A948E' }}>
                We curate the best products from Amazon to help you make smart shopping decisions.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium mb-4" style={{ color: '#2C302E' }}>Disclaimer</h3>
              <p className="text-sm" style={{ color: '#8A948E' }}>
                CartWhisper is an affiliate website. We may earn a commission when you purchase through our links.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium mb-4" style={{ color: '#2C302E' }}>Follow Us</h3>
              <a
                href="https://pinterest.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm"
                style={{ color: '#8CA692' }}
                data-testid="pinterest-link"
              >
                Pinterest →
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t text-center" style={{ borderColor: 'rgba(44, 48, 46, 0.08)' }}>
            <p className="text-sm" style={{ color: '#8A948E' }}>© 2024 CartWhisper. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;